import { COLOR, IllustrationFrame, paint, type IllustrationProps } from './frame.tsx';

export type { IllustrationProps } from './frame.tsx';

/** Round "mandatory" sign (blue disc, white arrow) on its pole: the theory questions. */
export function TheorySignIllustration({ width = 120, height = 120, ...rest }: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 120 120" {...rest}>
      <rect x="57" y="68" width="6" height="46" rx="1" style={paint(COLOR.road)} />
      <rect x="32" y="112" width="56" height="5" rx="2.5" style={paint(COLOR.road)} />
      <circle cx="60" cy="40" r="34" style={paint(COLOR.primary)} />
      <circle cx="60" cy="40" r="29" fill="none" style={paint('none', COLOR.onPrimary, 2)} />
      <path d="M60 20 74 36H66V58H54V36H46Z" style={paint(COLOR.onPrimary)} />
    </IllustrationFrame>
  );
}

/** Top view of a practice ground: lane with a centre line, a car and a slalom of cones. */
export function CourseIllustration({ width = 200, height = 140, ...rest }: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 200 140" {...rest}>
      <rect width="200" height="140" rx="16" style={paint(COLOR.grass)} />
      <rect x="10" y="44" width="180" height="60" rx="8" style={paint(COLOR.road)} />
      <path
        d="M22 74H178"
        fill="none"
        strokeDasharray="10 8"
        style={paint('none', COLOR.roadLine, 2)}
      />
      <rect x="28" y="60" width="46" height="26" rx="7" style={paint(COLOR.car)} />
      <rect x="54" y="64" width="12" height="18" rx="3" style={paint(COLOR.roadLine)} />
      <circle cx="104" cy="58" r="6" style={paint(COLOR.cone)} />
      <circle cx="104" cy="58" r="2.5" style={paint(COLOR.roadLine)} />
      <circle cx="132" cy="90" r="6" style={paint(COLOR.cone)} />
      <circle cx="132" cy="90" r="2.5" style={paint(COLOR.roadLine)} />
      <circle cx="160" cy="58" r="6" style={paint(COLOR.cone)} />
      <circle cx="160" cy="58" r="2.5" style={paint(COLOR.roadLine)} />
    </IllustrationFrame>
  );
}

/** Warning triangle with an exclamation mark: the "điểm liệt" (critical) questions. */
export function CriticalTriangleIllustration({
  width = 120,
  height = 120,
  ...rest
}: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 120 120" {...rest}>
      <path
        d="M60 12 112 102H8Z"
        strokeLinejoin="round"
        style={paint(COLOR.dangerBg, COLOR.danger, 7)}
      />
      <rect x="56" y="42" width="8" height="30" rx="4" style={paint(COLOR.danger)} />
      <circle cx="60" cy="85" r="5" style={paint(COLOR.danger)} />
    </IllustrationFrame>
  );
}

/** A blank sheet whose lines are drawn as road dashes: nothing in the list yet. */
export function EmptyListIllustration({ width = 160, height = 120, ...rest }: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 160 120" {...rest}>
      <rect
        x="40"
        y="10"
        width="80"
        height="98"
        rx="8"
        style={paint(COLOR.surface, COLOR.border, 2)}
      />
      <path
        d="M56 38H104M56 58H104M56 78H88"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="8 7"
        style={paint('none', COLOR.border, 4)}
      />
      <rect x="28" y="112" width="104" height="4" rx="2" style={paint(COLOR.road)} />
    </IllustrationFrame>
  );
}

/** "Cấm đi ngược chiều" (red disc, white bar) on its pole: the 404 page. */
export function NoEntryIllustration({ width = 120, height = 120, ...rest }: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 120 120" {...rest}>
      <rect x="57" y="68" width="6" height="46" rx="1" style={paint(COLOR.road)} />
      <rect x="32" y="112" width="56" height="5" rx="2.5" style={paint(COLOR.road)} />
      <circle cx="60" cy="40" r="34" style={paint(COLOR.danger)} />
      <rect x="36" y="32" width="48" height="16" rx="2" style={paint(COLOR.onDanger)} />
    </IllustrationFrame>
  );
}

/** Three traffic cones with two white bands each: the error pages. */
export function ConesIllustration({ width = 160, height = 120, ...rest }: IllustrationProps) {
  return (
    <IllustrationFrame width={width} height={height} viewBox="0 0 160 120" {...rest}>
      <rect x="10" y="108" width="140" height="4" rx="2" style={paint(COLOR.road)} />
      <Cone x={34} y={108} scale={0.9} />
      <Cone x={80} y={108} scale={1.2} />
      <Cone x={126} y={108} scale={0.9} />
    </IllustrationFrame>
  );
}

/** One cone, drawn around its bottom centre (0, 0). */
function Cone({ x, y, scale }: { readonly x: number; readonly y: number; readonly scale: number }) {
  return (
    <g transform={`translate(${String(x)} ${String(y)}) scale(${String(scale)})`}>
      <rect x="-15" y="-6" width="30" height="6" rx="2" style={paint(COLOR.cone)} />
      <path d="M-10-6-4-50H4L10-6Z" style={paint(COLOR.cone)} />
      <path d="M-8.64-16H8.64L7.55-24H-7.55Z" style={paint(COLOR.roadLine)} />
      <path d="M-6.73-30H6.73L5.64-38H-5.64Z" style={paint(COLOR.roadLine)} />
    </g>
  );
}
